import React from 'react';
import {useMutation} from 'react-query';
import {useLocation, useNavigate} from 'react-router-dom';
import {Button} from 'reactstrap';
import {RepeatIcon, SparklesIcon} from 'lucide-react';
import {MixModel, ProjectDataIndex, ProjectTabType} from '../../api/types.ts';
import useRemixMix from '../../api/mixes/useRemixMix.ts';
import {useAuthStateManager} from '../../hooks/useAuthStateManager';
import notify from '../../lib/notify.ts';
import unknownErrorToString from '../../lib/unknownErrorToString.ts';
import Icon from '../shared/Icon.tsx';

interface VibeGameActionsProps {
    project: MixModel;
    dataIndex: ProjectDataIndex;
}

/*
 * Turns someone using a published vibe-coded tab into someone making one. "Make
 * your own" creates a new project with an empty Vibe Code tab, which opens the
 * Vibe prompt; "Remix this" copies just this tab into a project of their
 * own and opens Vibe to change it. Both need an account, so signed out they go
 * through login first.
 */
const VibeGameActions: React.FC<VibeGameActionsProps> = ({
    project,
    dataIndex
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const {isAuthenticated} = useAuthStateManager();

    const tabs = project.tabs;
    const tabTitle =
        (tabs && tabs[`tab${dataIndex}` as keyof typeof tabs]?.title) || 'this';

    const remixMix = useRemixMix();
    const remixMutation = useMutation(remixMix, {
        onSuccess: result => {
            if (result) {
                notify.success('Your copy is ready.');
                // vibe=1 opens the Vibe assistant on the copy, ready to change it.
                navigate(
                    `/project/${result.id}?tab=${
                        result.tab ?? 'project'
                    }&vibe=1`
                );
            }
        },
        onError: (error: unknown) => {
            notify.error(
                'Unable to remix this. ' + unknownErrorToString(error)
            );
        }
    });

    const loginThen = (path: string) =>
        navigate(`/login?redir=${encodeURIComponent(path)}`);

    const makeYourOwn = () => {
        // start=1 creates the project at once, so the next thing they see is
        // the Vibe prompt (an empty Vibe Code tab opens it by itself).
        const startPage = `/new/_?type=${ProjectTabType.VibeCode}&start=1`;
        if (isAuthenticated) {
            navigate(startPage);
        } else {
            loginThen(startPage);
        }
    };

    const remix = () => {
        if (!isAuthenticated) {
            loginThen(location.pathname + location.search);
            return;
        }

        remixMutation.mutate({
            id: project.id,
            sourceTab: dataIndex,
            name: '',
            description: '',
            tags: [],
            thumbnail: null,
            replacements: []
        });
    };

    return (
        <div className="vibe-game-actions">
            <span className="vibe-game-actions-text">
                Made with vibe coding. Describe what you want and AI builds it.
            </span>
            <Button
                color="secondary"
                size="sm"
                className="btn-icon"
                onClick={remix}
                disabled={remixMutation.isLoading}
                title={`Make your own copy of ${tabTitle} to change`}
                aria-label="Remix this"
            >
                <Icon icon={RepeatIcon} size="s" />
                <span>
                    {remixMutation.isLoading ? 'Remixing…' : 'Remix this'}
                </span>
            </Button>
            <Button
                color="primary"
                size="sm"
                className="btn-icon"
                onClick={makeYourOwn}
                title="Start something new with vibe coding"
                aria-label="Make your own"
            >
                <Icon icon={SparklesIcon} size="s" />
                <span>Make your own</span>
            </Button>
        </div>
    );
};

export default VibeGameActions;
